import { notifySaveSuccess } from '../utils/saveNotification';
import {t} from '../localization/adminText';
import useDesignerDraft from '../components/CodFormDesigner/useDesignerDraft';
import DesignerActions from '../components/CodFormDesigner/DesignerActions';
import SettingsTabs from '../components/SettingsTabs';
import './CodSettings.css';
import CodCountryShipping from '../components/CodCountryShipping';
import { useSearchParams } from "react-router-dom";
import {
  Banner,
  BlockStack,
  Button,
  Card,
  Checkbox,
  Layout,
  Text,
} from '../components/admin-ui';
import { useCallback, useEffect, useRef, useState } from "react";

import {
  g_cod_settings,
  u_cod_settings,
} from "../api";
import ContentLocalization from "../components/ContentLocalization";
import CodFormDesigner from "../components/CodFormDesigner";
import AddCodFormToTheme from '../components/AddCodFormToTheme';
import CodExitIntentSettings from "../components/CodExitIntentSettings";
import CodRiskSettings from "../components/CodRiskSettings";
import CodUpsellSettings from "../components/CodUpsellSettings";
import storageUtils from "../utils/storageUtils";

const EMPTY_STYLE = {
  display_mode: "embedded",
  popup_button_label: "Order with cash on delivery",
  popup_button_full_width: true,
  popup_button_background_color: "#111827",
  popup_button_text_color: "#ffffff",
  popup_max_width: 760,
  popup_border_radius: 16,
  popup_overlay_opacity: 40,
  popup_sticky_footer: true,
  form_border_color: "#dedede",
  form_border_width: 1,
  text_color: "#333333",
  label_color: "#666666",
  placeholder_color: "#9ca3af",
  input_background_color: "#ffffff",
  input_border_color: "#dedede",
  input_border_width: 1,
  input_focus_border_color: "#111111",
  input_error_color: "#dc2626",
  input_border_radius: 6,
  input_height: 52,
  form_width: "theme",
  form_max_width: 760,
  form_background_color: "#ffffff",
  form_border_radius: 12,
  form_padding: 24,
  field_gap: 14,
  field_layout: "auto",
  mobile_single_column: true,
  show_shipping_method: true,
  show_order_total: true,
  show_cod_note: true,
  form_title: "Shipping information",
  form_subtitle: "Enter your shipping details and we'll arrange delivery.",
  submit_button_label: "Place COD order",
  order_total_label: "Total",
  cod_note_label: "Cash on delivery (COD)",
  order_total_text_color: "#333333",
  order_total_background_color: "#ffffff",
  order_total_font_size: 20,
  order_total_padding: 14,
  order_total_border_radius: 0,
  submit_button_active_background_color: "#374151",
  submit_button_font_size: 16,
  submit_button_height: 48,
  submit_button_border_radius: 8,
  submit_button_background_color: "#111827",
  submit_button_text_color: "#ffffff",
  submit_button_full_width: true,
};

const EMPTY_SETTINGS = {
  currency: "USD",
  countries: [],
  country_catalog: [],
  google_address_enabled: true,
  custom_result_page_enabled: false,
  google_address_configured: false,
  form_schema: [],
  available_fields: [],
  shipping_options: [],
  style: EMPTY_STYLE,
  ip_whitelist: [],
  max_orders_per_ip_hour: 5,
  max_orders_per_phone_day: 3,
  cod_discount_rules: { type: "percentage", value: "10", minimum_amount: "0", minimum_quantity: 0,
    amount_message: "Add {remaining_amount} more to use {discount_code}.",
    quantity_message: "Add {remaining_quantity} more item(s) to use {discount_code}.",
    both_message: "Add {remaining_amount} more and {remaining_quantity} more item(s) to use {discount_code}." },
  exit_intent_enabled: false,
  exit_intent_discount_code: "",
  exit_intent_desktop_enabled: true,
  exit_intent_hesitation_enabled: true,
  exit_intent_mobile_enabled: true,
  exit_intent_delay_seconds: 30,
  exit_intent_inactivity_seconds: 20,
  exit_intent_frequency_hours: 24,
  exit_intent_title: "Wait — your discount is ready",
  exit_intent_description: "Complete your cash-on-delivery order now and save.",
  exit_intent_button_label: "Continue my order",
  exit_intent_dismiss_label: "No thanks",
};

const settingsFromResponse = (data = {}) => ({
  ...EMPTY_SETTINGS,
  ...data,
  style: { ...EMPTY_STYLE, ...(data.style || {}) },
});


const messageFromError = (error, fallback) => {
  const body = error?.response?.data || {};
  const errors = body.errors || body.data?.errors;
  const first = errors ? Object.values(errors).flat()[0] : null;
  return first || body.msg || body.message || error?.message || fallback;
};

const isHttpsUrl = (value) => {
  try {
    return new URL(value).protocol === "https:";
  } catch {
    return false;
  }
};

const DESIGNER_NUMBER_KEYS = new Set([
  "popup_max_width",
  "popup_border_radius",
  "popup_overlay_opacity",
  "order_total_font_size",
  "order_total_padding",
  "order_total_border_radius",
  "submit_button_font_size",
  "submit_button_height",
  "submit_button_border_radius",
  "form_border_width",
  "input_border_width",
  "input_border_radius",
  "input_height",
  "form_max_width",
  "form_border_radius",
  "form_padding",
  "field_gap",
]);

const DESIGNER_BOOLEAN_KEYS = new Set([
  "popup_button_full_width",
  "popup_sticky_footer",
  "mobile_single_column",
  "show_shipping_method",
  "show_order_total",
  "show_cod_note",
  "submit_button_full_width",
]);

export default function CodSettings() {
  const userToken = storageUtils.get("user_token");
  const draft = useDesignerDraft(EMPTY_SETTINGS);
  const { settings, setSettings, load: acceptLoaded, accept: acceptSaved, dirty: designerDirty } = draft;
  const savePending = useRef(false);
  const [translationMode,setTranslationMode]=useState(false);
  const [languageActions,setLanguageActions]=useState(null);
  const [upsellActions,setUpsellActions]=useState(null);
  const upsellGuard = useRef(null);
  const setUpsellGuard = useCallback(guard => { upsellGuard.current = guard; }, []);
  const [searchParams, setSearchParams] = useSearchParams();
  const tabNames = ['settings', 'form-designer', 'upsell', 'risk-management', 'exit-intent'];
  const tabs = ['Settings', 'Form designer', 'Upsell', 'Risk management', 'Discounts & exit intent'].map((content, index) => ({id: tabNames[index], content}));
  const selectedTab = Math.max(0, tabNames.indexOf(searchParams.get('tab')));
  const setSelectedTab = index => { if (index===selectedTab) return; if (selectedTab===2 && upsellGuard.current && !upsellGuard.current()) return; if (selectedTab===1 && (savePending.current || (designerDirty && !window.confirm(t('Leave this page and discard unsaved form changes?'))))) return; if (selectedTab===1 && designerDirty) draft.discard(); const next = new URLSearchParams(searchParams); next.set('tab', tabNames[index]); setSearchParams(next); };
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saveVersion, setSaveVersion] = useState(0);
  const [notice, setNotice] = useState(null);

  const loadRequest = useRef(null);
  const loadSettings = useCallback(() => {
    if (loadRequest.current) return loadRequest.current;
    const request = (async () => {
    if (!userToken) {
      setNotice({ tone: "critical", message: "Store login token is missing." });
      setLoading(false);
      return false;
    }
    try {
      setLoading(true);
      setNotice(null);
      const response = await g_cod_settings({ user_token: userToken });
      acceptLoaded(settingsFromResponse(response.data));
      return true;
    } catch (error) {
      setNotice({ tone: "critical", message: messageFromError(error, "Unable to load COD settings.") });
      return false;
    } finally {
      setLoading(false);
    }
    })();
    loadRequest.current = request;
    request.finally(() => { if (loadRequest.current === request) loadRequest.current = null; });
    return request;
  }, [userToken, acceptLoaded]);

  useEffect(() => { loadSettings(); }, [loadSettings]);

  const update = (key, value) => {
    setSettings((current) => ({ ...current, [key]: value }));
    setNotice(null);
  };

  const save = async () => {
    if (savePending.current || loading) return false;
    const submitted = settings;
    const invalidIconField = settings.form_schema.find(
      (field) => field.icon_mode === "custom" && !isHttpsUrl(String(field.icon_url || "").trim()),
    );
    if (invalidIconField) {
      setNotice({ tone: "critical", message: `Enter a valid HTTPS image URL for ${invalidIconField.label}.` });
      return false;
    }
    const normalizedStyle = Object.fromEntries(Object.entries(settings.style).map(([key, value]) => [
      key,
      DESIGNER_NUMBER_KEYS.has(key)
        ? Number(value)
        : DESIGNER_BOOLEAN_KEYS.has(key)
          ? (value ? 1 : 0)
          : value,
    ]));
    const normalizedFields = settings.form_schema.map((field) => ({
      ...field,
      ...(field.name === "product_information" ? Object.fromEntries(
        ["show_image", "show_variant", "show_quantity", "show_price"].map((key) => [key, field[key] !== false && field[key] !== 0 ? 1 : 0]),
      ) : {}),
      required: field.required ? 1 : 0,
      show_label: field.show_label ? 1 : 0,
      show_placeholder: 1,
      floating_label: field.show_label && field.floating_label ? 1 : 0,
      use_global_style: field.use_global_style !== false ? 1 : 0,
      icon_mode: field.icon_mode || "default",
      icon_url: field.icon_mode === "custom" ? String(field.icon_url || "").trim() : "",
      icon_size: Number(field.icon_size ?? 20),
      style: {
        ...field.style,
        input_height: Number(field.style?.input_height ?? 52),
        input_border_width: Number(field.style?.input_border_width ?? 1),
        input_border_radius: Number(field.style?.input_border_radius ?? 6),
      },
    }));
    try {
      savePending.current = true;
      setSaving(true);
      setNotice(null);
      const response = await u_cod_settings({
        user_token: userToken,
        countries: settings.countries,
        google_address_enabled: settings.google_address_enabled ? 1 : 0,
        custom_result_page_enabled: settings.custom_result_page_enabled ? 1 : 0,
        form_schema: normalizedFields,
        style: normalizedStyle,
        ip_whitelist: settings.ip_whitelist || [],
        max_orders_per_ip_hour: Number(settings.max_orders_per_ip_hour),
        max_orders_per_phone_day: Number(settings.max_orders_per_phone_day),
        cod_discount_rules: settings.cod_discount_rules,
        exit_intent_enabled: settings.exit_intent_enabled ? 1 : 0,
        exit_intent_discount_code: settings.exit_intent_discount_code.trim(),
        exit_intent_desktop_enabled: settings.exit_intent_desktop_enabled ? 1 : 0,
        exit_intent_hesitation_enabled: settings.exit_intent_hesitation_enabled ? 1 : 0,
        exit_intent_mobile_enabled: settings.exit_intent_mobile_enabled ? 1 : 0,
        exit_intent_delay_seconds: Number(settings.exit_intent_delay_seconds),
        exit_intent_inactivity_seconds: Number(settings.exit_intent_inactivity_seconds),
        exit_intent_frequency_hours: Number(settings.exit_intent_frequency_hours),
        exit_intent_title: settings.exit_intent_title,
        exit_intent_description: settings.exit_intent_description,
        exit_intent_button_label: settings.exit_intent_button_label,
        exit_intent_dismiss_label: settings.exit_intent_dismiss_label,
      });
      if (!response.data || Number(response.status ?? response.code ?? 200) !== 200) throw new Error(response.msg || 'Unable to save COD settings.');
      const fullySaved = acceptSaved(settingsFromResponse(response.data), submitted);
      setNotice({ tone: "success", message: fullySaved ? (response.msg || "COD settings saved.") : t("Earlier changes saved. Your latest edits still need saving.") });
      notifySaveSuccess(fullySaved ? 'Changes saved' : 'Earlier changes saved. Your latest edits still need saving.');
      setSaveVersion((version) => version + 1);
      return fullySaved;
    } catch (error) {
      setNotice({ tone: "critical", message: messageFromError(error, "Unable to save COD settings.") });
      return false;
    } finally {
      savePending.current = false;
      setSaving(false);
    }
  };

  const languageSlot = <div className="cod-header-language" ref={setLanguageActions}/>;
  const saveAction = <div className="cod-header-primary"><Button variant="primary" inlineSize="fill" onClick={save} loading={saving} disabled={loading||!userToken}>{t('Save changes')}</Button></div>;
  const themeAction = <AddCodFormToTheme disabled={loading||saving} beforeOpen={async()=>{
    if (!designerDirty) return true;
    if (!window.confirm(t('Save your form changes before opening the theme editor?'))) return false;
    return save();
  }}/>;

  return (
    <main className="cod-settings-page">
      <header className="cod-page-header">
        <div className="cod-page-title-row">
          <div className="cod-page-title"><h1>{t('Cash on delivery')}</h1><p>{t('Configure COD availability, delivery rates and the customer form.')}</p></div>
          <div className="cod-header-actions">
            {selectedTab===1 ? <DesignerActions inline showActions={!translationMode} dirty={designerDirty} saving={saving} disabled={loading||!userToken} onSave={save} onDiscard={()=>{draft.discard();setNotice(null);}}>{languageSlot}</DesignerActions>
              : selectedTab===2 ? <>{languageSlot}<div className="cod-header-section-actions" ref={setUpsellActions}/></>
              : selectedTab===4 ? <>{languageSlot}{!translationMode&&saveAction}</>
              : saveAction}
          </div>
        </div>
        <SettingsTabs tabs={tabs} selected={selectedTab} onSelect={setSelectedTab}/>
      </header>
      <div className="cod-page-content">
        <BlockStack gap="400">
          {notice ? <Banner tone={notice.tone}>{notice.message}</Banner> : null}
          {selectedTab === 0 ? (
            <Layout><Layout.Section><BlockStack gap="400">
              <Card><BlockStack gap="400">
                <Text as="h2" variant="headingMd">General</Text>
                <Checkbox label="Enable Google address autocomplete" checked={settings.google_address_enabled} onChange={(value) => update("google_address_enabled", value)} helpText="Uses the Google Maps key configured for this store." disabled={loading} />
                <Checkbox label="Use custom COD order result page" checked={settings.custom_result_page_enabled} onChange={(value) => update("custom_result_page_enabled", value)} helpText="Off: open the Shopify order status page (the fulfillment store for A/B orders). On: show the custom result page through this store's App Proxy, keeping the storefront domain." disabled={loading} />
                {!loading && settings.google_address_enabled && !settings.google_address_configured ? (
                  <Banner tone="warning">
                    Google address suggestions are unavailable because this store has no Google Maps API key. Add one under Integrations → Google Maps, then make sure the key can use Maps JavaScript API and Places API (Legacy).
                  </Banner>
                ) : null}
              </BlockStack></Card>

              <CodCountryShipping settings={settings} userToken={userToken} disabled={loading||saving}
                onChange={data=>setSettings(current=>({...current,countries:data.countries,removed_countries:data.removed_countries,shipping_options:data.shipping_options,country_catalog:data.country_catalog}))}/>

            </BlockStack></Layout.Section></Layout>
          ) : selectedTab === 1 ? (
            <ContentLocalization key="form" namespace="form" beforeLanguageChange={async()=>{
              if (!designerDirty) return true;
              if (!window.confirm(t('Save your form changes before editing translations?'))) return false;
              return save();
            }} headerTarget={languageActions} onSourceRefresh={loadSettings} settings={settings} loading={loading||saving} onModeChange={setTranslationMode}>{({settings:localized,editor,locale,isDefault})=><CodFormDesigner settings={localized} setSettings={isDefault?setSettings:()=>{}} onSave={isDefault?save:async()=>false} onReset={loadSettings} dirty={designerDirty} onChange={()=>setNotice(null)} saving={saving} loading={loading} saveVersion={saveVersion} translationEditor={editor} contentLocale={locale} themeAction={themeAction}/>}</ContentLocalization>
          ) : selectedTab === 2 ? (
            <CodUpsellSettings actionTarget={upsellActions} onBeforeLeaveChange={setUpsellGuard} headerTarget={languageActions} settings={settings} onSourceRefresh={loadSettings} onModeChange={setTranslationMode}/>
          ) : selectedTab === 3 ? (
            <CodRiskSettings showSaveAction={false} settings={settings} update={update} onSave={save} saving={saving} loading={loading} userToken={userToken} />
          ) : (
            <ContentLocalization key="discounts" namespace="discounts" headerTarget={languageActions} onSourceRefresh={loadSettings} settings={settings} loading={loading||saving} onModeChange={setTranslationMode}>{({settings:localized,editor,locale,isDefault})=><CodExitIntentSettings settings={localized} setSettings={isDefault?setSettings:()=>{}} loading={loading} translationEditor={editor} contentLocale={locale}/>}</ContentLocalization>
          )}
        </BlockStack>
      </div>
    </main>
  );
}
